import { z } from 'zod';

import { normalizeDateToISOString } from '@vestigas/utils';
import { i18n } from '~/utils';

import {
  ledgerAccountImportHeaderFields,
  ledgerAccountImportMandatoryHeaderFields,
  ledgerAccountImportDefaultColumnMapping,
} from './ledgerAccountImportSchema';

// The import file is always German bookkeeping terminology, independent of
// the app's UI locale, so the accepted values are the literal German labels
// rather than a locale-driven translation of the internal account type codes.
// The values are identical to accountTypes[accountType] in i18n ns: 'ledgerAccounts'.
const LEDGER_ACCOUNT_TYPE_IMPORT_LABELS = [
  'Aktivkonto',
  'Aufwandskonto',
  'Passivkonto',
  'Ertragskonto',
] as const;

const zodLedgerAccountImportFormBaseSchema = z.object({
  validFrom: z
    .string()
    .optional()
    .refine((value) => !value || z.iso.date().safeParse(value).success, {
      message: i18n.t(($) => $.import.form.errors.validFromIsInvalid, {
        ns: 'ledgerAccounts',
      }),
    }),
});

export const zodLedgerAccountImportFormSchema = z.discriminatedUnion(
  'applyToAllCompanies',
  [
    zodLedgerAccountImportFormBaseSchema.extend({
      applyToAllCompanies: z.literal(false),
      companyIds: z.array(z.uuid()).min(1, {
        message: i18n.t(($) => $.import.form.errors.companyIsRequired, {
          ns: 'ledgerAccounts',
        }),
      }),
    }),
    zodLedgerAccountImportFormBaseSchema.extend({
      applyToAllCompanies: z.literal(true),
      companyIds: z.array(z.uuid()).optional(),
    }),
  ],
);
const formatOptionsList = (options: string[]) =>
  new Intl.ListFormat('de', {
    type: 'disjunction',
    style: 'long',
  }).format(options);

export const zodLedgerAccountImportRowsSchema = z.array(
  z.object({
    number: z.coerce.string().trim().min(1),
    account_type: z.preprocess(
      (value) => {
        const trimmed = typeof value === 'string' ? value.trim() : value;

        return trimmed === '' ? undefined : trimmed;
      },
      z
        .enum(LEDGER_ACCOUNT_TYPE_IMPORT_LABELS, {
          error: i18n.t(($) => $.import.validation.accountTypeIsInvalid, {
            ns: 'ledgerAccounts',
            options: formatOptionsList(LEDGER_ACCOUNT_TYPE_IMPORT_LABELS),
          }),
        })
        .optional(),
    ),
    cost_type: z.string().trim().optional(),
    name: z.string().trim().optional(),
    // XLSX date cells arrive as Excel serial numbers, CSV dates as strings.
    // The server parses the uploaded file itself, so an unparseable date is
    // dropped locally rather than blocking the dry-run that reports it.
    valid_from: z
      .preprocess(
        (value) => (value ? normalizeDateToISOString(value) : undefined),
        z.iso.date().optional(),
      )
      .catch(undefined),
  }),
);

const optionalHeaderFields = ledgerAccountImportHeaderFields.filter(
  (header) =>
    !ledgerAccountImportMandatoryHeaderFields.some(
      (mandatoryHeader) => mandatoryHeader === header,
    ),
);

export const zodLedgerAccountImportHeadersSchema = z
  .array(z.string())
  .superRefine((headers, context) => {
    const headerSet = new Set(headers);

    for (const header of ledgerAccountImportMandatoryHeaderFields) {
      if (!headerSet.has(header)) {
        context.addIssue({
          code: 'custom',
          message: i18n.t(($) => $.import.validation.columnIsRequired, {
            column: ledgerAccountImportDefaultColumnMapping[header],
            ns: 'ledgerAccounts',
          }),
          params: {
            errorCode: 'mandatory',
            header,
          },
          path: [header],
        });
      }
    }

    for (const header of optionalHeaderFields) {
      if (!headerSet.has(header)) {
        context.addIssue({
          code: 'custom',
          message: i18n.t(($) => $.import.validation.columnIsOptional, {
            column: ledgerAccountImportDefaultColumnMapping[header],
            ns: 'ledgerAccounts',
          }),
          params: {
            errorCode: 'optional',
            header,
          },
          path: [header],
        });
      }
    }
  });
